/* eslint-disable camelcase */
import React, { useState } from "react";
import { Formik } from "formik";
import validator from "validator";

import {
  Form,
  Loader,
  Heading,
  Container,
  Row,
  Column,
  DetailsItem,
  ActionBar,
  TextInput,
  SelectBox,
  ButtonGroup,
  SubmitButton,
  CancelButton,
  apiShape,
  intlShape,
  browserShape,
  notifierShape,
  withApi,
  withIntl,
  withBrowser,
  withNotifier,
  withAccessRights,
  handleFormikValueChange,
  capitalizeWords,
  setEmptyOrUndefinedToNull,
  removeNullAttributes,
} from "lcm-iot-commons";

const ENUM_DHCP = "DHCP";
const ENUM_MANUAL = "manual";

export function extractInitialValues(response: $TSFixMe, intl: $TSFixMe) {
  const initialValues = { ...response };
  initialValues.type =
    response.number !== undefined
      ? `${response.type} ${response.number}`
      : response.type;
  initialValues.modus = {
    key: response.modus,
    name: intl.formatMessage({
      id: `enum.network_interface_modus.${response.modus.toLowerCase()}`,
    }),
  };
  return initialValues;
}

export function createPayload(values: $TSFixMe) {
  const { name, modus, ip_address, subnet_mask } = values;
  let payload: {
    modus: string;
    name: string | undefined;
    ip_address?: string;
    subnet_mask?: string;
  } = {
    modus: modus.key,
    name: name?.trim(),
  };
  if (modus.key === ENUM_MANUAL) {
    payload = {
      ...payload,
      ip_address,
      subnet_mask,
    };
  }
  return setEmptyOrUndefinedToNull(payload);
}

export const validateAccessRights = (accessRights: $TSFixMe) =>
  accessRights.canUpdate;

type OwnNetworkInterfaceEditProps = {
  api: apiShape;
  intl: intlShape;
  browser: browserShape;
  notifier: notifierShape;
  match: {
    params?: {
      id?: string;
      networkInterfaceId?: string;
    };
  };
};

const defaultProps: Partial<OwnNetworkInterfaceEditProps> = {};

type NetworkInterfaceEditProps = OwnNetworkInterfaceEditProps &
  typeof defaultProps;

export function NetworkInterfaceEdit(props: NetworkInterfaceEditProps) {
  const { api, intl, browser, match, notifier } = props;
  // @ts-expect-error TS(2339) FIXME: Property 'id' does not exist on type '{ id?: strin... Remove this comment to see the full error message
  const { id, networkInterfaceId } = match.params;
  const [initialValues, setInitialValues] = useState();

  const loadData = async () => {
    try {
      const response = await api.get(
        `/edm/edge_devices/${id}/network_interfaces/${networkInterfaceId}`,
      );
      if (response.type === "WAN") {
        browser.navigateTo("/404");
      }
      setInitialValues(extractInitialValues(response, intl));
    } catch (error) {
      notifier.showError(api.translateError(error));
      // @ts-expect-error TS(2345) FIXME: Argument of type '{}' is not assignable to paramet... Remove this comment to see the full error message
      setInitialValues({});
    }
  };
  React.useEffect(() => {
    loadData();
  }, [id]);

  const onSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    const payload = createPayload(values);
    try {
      await api.patch(
        `/edm/edge_devices/${id}/network_interfaces/${networkInterfaceId}`,
        payload,
      );
      notifier.showSuccess(
        intl.formatMessage({
          id: "network_interface_edit.success_notification",
        }),
      );
      browser.goBack();
    } catch (error) {
      notifier.showError(api.translateError(error));
    } finally {
      actions.setSubmitting(false);
    }
  };

  const validateForm = (values: $TSFixMe) => {
    const errors = {};
    const { modus, ip_address, subnet_mask } = values;
    const validIpHint = intl.formatMessage({
      id: "validation.ip_address.invalid",
    });

    if (modus.key === ENUM_MANUAL) {
      (errors as $TSFixMe).ip_address = validator.isIP(ip_address || "")
        ? null
        : validIpHint;
      (errors as $TSFixMe).subnet_mask = validator.isIP(subnet_mask || "")
        ? null
        : validIpHint;
    }
    return removeNullAttributes(errors);
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting, values } = formProps;
    const { type } = values;
    const isDhcp = values.modus.key === ENUM_DHCP;
    const isManualMode = values.modus.key === ENUM_MANUAL;
    return (
      <Form {...formProps}>
        <DetailsItem
          id="edge-device-type"
          translationKey="label.type"
          value={type}
        />
        <TextInput
          {...formProps}
          id="name"
          name="name"
          label={intl.formatMessage({ id: "label.name" })}
        />
        <h2 id="section-ip-configuration">
          {intl.formatMessage({
            id: "network_interface_details.ip_configuration",
          })}
        </h2>
        <SelectBox
          {...formProps}
          id="modus"
          name="modus"
          valueKey="key"
          label={intl.formatMessage({ id: "label.modus" })}
          options={[
            { id: 0, key: ENUM_MANUAL, name: capitalizeWords(ENUM_MANUAL) },
            { id: 1, key: ENUM_DHCP, name: ENUM_DHCP },
          ]}
          handleChange={(event) =>
            handleFormikValueChange(
              { ...formProps, key: "modus", name: "modus" },
              event.target.value,
            )
          }
          required
        />
        {isManualMode && (
          <>
            <TextInput
              {...formProps}
              id="ip-address"
              name="ip_address"
              label={intl.formatMessage({ id: "label.ip_address" })}
              placeholder={intl.formatMessage({ id: "general.default_ip" })}
              required={isManualMode}
              disabled={isDhcp}
            />
            <TextInput
              {...formProps}
              id="subnet-mask"
              name="subnet_mask"
              label={intl.formatMessage({ id: "label.subnet_mask" })}
              placeholder={intl.formatMessage({ id: "general.default_ip" })}
              required={isManualMode}
              disabled={isDhcp}
            />
          </>
        )}
        <ButtonGroup>
          <SubmitButton
            id="edit-submit-button"
            fetching={!initialValues || isSubmitting}
            disabled={!formProps.dirty || isSubmitting}
          />
          <CancelButton
            id="edit-cancel-button"
            fetching={!initialValues || isSubmitting}
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; fetching: true; disabled: any;... Remove this comment to see the full error message
            disabled={isSubmitting}
          />
        </ButtonGroup>
      </Form>
    );
  };

  return (
    <Container>
      <Row>
        <Column>
          <ActionBar>
            <Heading
              title={intl.formatMessage({
                id: "network_interface_edit.header",
              })}
            />
          </ActionBar>
        </Column>
      </Row>
      {initialValues && (
        <Row>
          <Column>
            <Formik
              id="network-interface-form"
              onSubmit={onSubmit}
              validate={validateForm}
              initialValues={initialValues}
            >
              {renderForm}
            </Formik>
          </Column>
        </Row>
      )}
      <Loader loading={!initialValues} />
    </Container>
  );
}

NetworkInterfaceEdit.defaultProps = defaultProps;

export default withBrowser(
  withApi(
    // @ts-expect-error TS(2345) FIXME: Argument of type 'ForwardRefExoticComponent<Omit<W... Remove this comment to see the full error message
    withIntl(
      withNotifier(
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ (props: Readonly<Omit<WithAcce... Remove this comment to see the full error message
        withAccessRights(
          // @ts-expect-error TS(2345) FIXME: Argument of type 'typeof NetworkInterfaceEdit' is ... Remove this comment to see the full error message
          NetworkInterfaceEdit,
          "EDM::EdgeDevice",
          validateAccessRights,
        ),
      ),
    ),
  ),
);
